import type { Appointment, Reference } from "@medplum/fhirtypes";
import { ORDER_SET_ENTITY_ID_SYSTEM } from "@repo/api";
import { isSchedulingResolved } from "../lib/procedure-lifecycle";

/**
 * Derived scheduling lens over a curated treatment plan's requests.
 *
 * "Scheduled" is **derived, not stored** (see
 * `Schedule Curated Requests into Appointments.md`): a request carries no
 * scheduling status of its own. Its state is read from two signals that already
 * exist in FHIR —
 *
 *   - outcome: a Procedure recorded against the request — `completed` means it
 *     happened, `not-done` means it was explicitly skipped. Both read off the
 *     Procedure, never off a Task: those rows are the work queue's private
 *     storage and its evaluator is their only writer, while the clinical
 *     outcome belongs on the event resource;
 *   - booking: a non-cancelled `Appointment` naming this request's OWNING ORDER
 *     SET in `supportingInformation[]`.
 *
 * Booking granularity is the SET, not the order. A set is booked as a whole —
 * every live order, at most once, one execution — so every live order in it
 * shares one booking state, and a visit booked for the set schedules all of
 * them. The check is array-membership over `supportingInformation`, which the
 * read-side query builder can't express, so it resolves here in pure code — the
 * same code-side idiom the protocol grouping uses. This module is pure (no I/O)
 * and is the single place every scheduling read flows through.
 */

/** Per-request scheduling state. Terminal states (`delivered`/`notPerformed`) win over a booking. */
export type RequestSchedulingStatus = "unscheduled" | "scheduled" | "delivered" | "notPerformed";

/** Roll-up over a set of requests (a protocol group, or the whole plan). */
export type GroupSchedulingStatus = "unscheduled" | "scheduled" | "delivered";

/** Does this appointment carry out the given order set? */
function bindsOrderSet(apt: SchedulingAppointment, orderSetId: string): boolean {
  return (apt.supportingInformation ?? []).some(
    (ref) => ref.identifier?.system === ORDER_SET_ENTITY_ID_SYSTEM && ref.identifier.value === orderSetId,
  );
}

/** Appointment statuses that do NOT bind a request to a booking. */
const NON_BINDING_APPOINTMENT_STATUSES: ReadonlySet<Appointment["status"]> = new Set([
  "cancelled",
  "noshow",
  "entered-in-error",
]);

export interface SchedulingRequestInput {
  /** The order's member id; unique inside the one set a projection covers. */
  orderId: string;
  /** A `completed` Procedure was recorded against this request — it happened. */
  performed?: boolean;
  /** A `not-done` Procedure was recorded against this request — explicitly skipped, not failed. */
  notPerformed?: boolean;
}

/** Minimal Appointment shape the deriver reads. */
export interface SchedulingAppointment {
  id?: string;
  domainId?: string;
  status: Appointment["status"];
  start?: string;
  supportingInformation?: Reference[];
}

export interface RequestSchedulingState {
  schedulingStatus: RequestSchedulingStatus;
  /** The booked appointment binding this request (earliest start), when scheduled. */
  visitId?: string;
  /** That appointment's start, for surfacing a scheduled date on the worklist. */
  scheduledStart?: string;
}

/** The set owns the booking, including when it has no procedure requests. */
export function bindingOrderSetAppointment(
  appointments: SchedulingAppointment[],
  orderSetId?: string,
): SchedulingAppointment | undefined {
  // Earliest binding appointment for the set (no-start sorts last; stable otherwise).
  const sorted = [...appointments].sort((a, b) => (a.start ?? "￿").localeCompare(b.start ?? "￿"));
  return orderSetId
    ? sorted.find((apt) => !NON_BINDING_APPOINTMENT_STATUSES.has(apt.status) && bindsOrderSet(apt, orderSetId))
    : undefined;
}

/**
 * Derive each request's scheduling state from the patient's appointments.
 *
 * Per-request precedence: delivered → notPerformed → scheduled (bound to a
 * non-cancelled appointment) → unscheduled. The binding appointment (and its
 * date) is attached whenever one exists, regardless of terminal state — a
 * delivered procedure still carries the date it was seen. When several
 * appointments carry out the set, the earliest-start binds.
 *
 * BOTH TERMINAL STATES COME FROM THE PROCEDURE, not from a work-queue Task.
 * Delivery used to read `Task.status === "completed"`, but that Task is itself
 * a projection of the very Procedure read here: the producer's claim joins on
 * `Procedure.basedOn` and its contract settles on `completed`/`not-done`. So
 * the Task route was a cached copy of this fact, arriving an async bot hop
 * later — and it made a clinical state depend on the work queue, which owns
 * ownership and board position, not lifecycle. It also split one fact across
 * two sources: `notPerformed` already came straight from the Procedure, so a
 * lagging or unminted Task showed a charted procedure as merely scheduled
 * while its skipped sibling resolved correctly.
 *
 * Non-terminal Procedure statuses (`preparation`, `in-progress`) are not an
 * outcome and fall through to the booking, exactly as the producer's contract
 * treats them.
 *
 * Every request in the set shares that one binding: the set is what gets booked.
 * Without `orderSetId` there is nothing to match on, so nothing binds.
 */
export function deriveRequestSchedulingStates(
  requests: SchedulingRequestInput[],
  appointments: SchedulingAppointment[],
  orderSetId?: string,
): Map<string, RequestSchedulingState> {
  const binding = bindingOrderSetAppointment(appointments, orderSetId);

  const states = new Map<string, RequestSchedulingState>();
  for (const r of requests) {
    const apt = binding;
    const bound = apt
      ? { ...(apt.domainId ? { visitId: apt.domainId } : {}), ...(apt.start ? { scheduledStart: apt.start } : {}) }
      : {};

    let schedulingStatus: RequestSchedulingStatus;
    if (r.performed) schedulingStatus = "delivered";
    else if (r.notPerformed) schedulingStatus = "notPerformed";
    else if (apt) schedulingStatus = "scheduled";
    else schedulingStatus = "unscheduled";

    states.set(r.orderId, { schedulingStatus, ...bound });
  }
  return states;
}

/**
 * Roll a group's per-request statuses into a group status.
 *
 * A protocol is booked whole, so any request out of the backlog (booked or
 * resolved) schedules the group. Once every request is resolved the session
 * happened and the group is delivered; each request keeps its own outcome.
 */
export function rollupGroupSchedulingStatus(statuses: RequestSchedulingStatus[]): GroupSchedulingStatus {
  if (statuses.length === 0) return "unscheduled";
  if (statuses.every(isSchedulingResolved)) return "delivered";
  if (statuses.some((s) => isSchedulingResolved(s) || s === "scheduled")) return "scheduled";
  return "unscheduled";
}
